/**
 * Bottom-sheet drag behaviour for modals.
 *
 * - Opens sized to its content (so e.g. the cart's "You may also like" row is
 *   visible without dragging), capped at MAX_VH.
 * - Dragging the handle/header follows the finger smoothly (the cap grows/shrinks
 *   1:1), instead of snapping straight to full.
 * - Dragging down past the minimum translates the sheet away → dismiss.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const MAX_VH = 92;
const MIN_VH = 32;
const DISMISS_PX = 100;

/** The height the shopper can SEE, read now — never a number kept from the first paint.
 *  `visualViewport` is what an iOS in-app browser (Instagram, Facebook: WKWebView) changes when its
 *  toolbar moves; `innerHeight` is the fallback for a browser without it. */
function visibleHeight() {
  if (typeof window === 'undefined') return 700;
  const vv = window.visualViewport;
  return (vv && vv.height > 0 ? vv.height : window.innerHeight) || 700;
}

/** The room the overlay leaves the sheet: its own height less its padding, which is where the
 *  notch / status bar (`env(safe-area-inset-top)`) is paid. `Infinity` before it is laid out. */
function roomInOverlay(sheet) {
  const overlay = sheet && sheet.parentElement;
  if (!overlay || !(overlay.clientHeight > 0) || typeof window === 'undefined' || !window.getComputedStyle) {
    return Infinity;
  }
  const cs = window.getComputedStyle(overlay);
  return overlay.clientHeight - (parseFloat(cs.paddingTop) || 0) - (parseFloat(cs.paddingBottom) || 0);
}

export function useDraggableSheet({ onDismiss, enabled = true }) {
  const contentRef = useRef(null);
  const [maxH, setMaxH] = useState(null); // px cap; null until first measure
  const [translateY, setTranslateY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startRef = useRef(null);
  const userSetRef = useRef(false); // once the user drags, stop auto-sizing
  const [canExpand, setCanExpand] = useState(false);

  const px = (vh) => (visibleHeight() * vh) / 100;

  // Auto-size to content (incl. recommendations) until the user drags.
  useEffect(() => {
    const measure = () => {
      const el = contentRef.current;
      if (!el) return;
      const max = Math.min(px(MAX_VH), roomInOverlay(el.parentElement));
      const min = Math.min(px(MIN_VH), max);
      /* THE CHROME ROUND THE SCROLL AREA IS MEASURED, NOT ESTIMATED.
       *
       * `maxHeight` is applied to the SHEET, and everything in this app is
       * `box-sizing: border-box` (`src/index.css`), so that number is the
       * sheet's OUTER height. `+ 72` was a hand-count of two of the three
       * things that live outside the scroll area — the drag handle and the
       * header — and of the third, the sheet's own padding, it counted
       * nothing. The cart sheet pads itself by `env(safe-area-inset-bottom)`:
       * 34px on an iPhone, taken straight out of the scroll area's share.
       *
       * So a two-line cart that fits on any screen opened ALREADY SCROLLED —
       * 34px of travel on a phone, ~2px everywhere else from the hand-count
       * being a little short — with its last row jammed against the inset.
       * That is the owner's "why in the cart having the scrolling indicator as
       * well" and "the cart showing too close to the bottom", one term apart.
       *
       * `sheet.offsetHeight - scroller.offsetHeight` IS the chrome, exactly:
       * whatever the sheet's current cap, both shrink together and the
       * difference is invariant. No constant to go stale when a row is added
       * to the header, and right for any consumer whose sheet pads itself
       * differently. The 72 survives only for the case where nothing has been
       * laid out yet (offsetHeight 0 — jsdom, or a first paint), where the
       * padding is read off computed style instead. */
      const box = el.parentElement;
      const cs = box && typeof window !== 'undefined' && window.getComputedStyle
        ? window.getComputedStyle(box)
        : null;
      const pad = cs
        ? (parseFloat(cs.paddingBottom) || 0) + (parseFloat(cs.paddingTop) || 0)
        : 0;
      const laidOut = box && box.offsetHeight > 0 && el.offsetHeight > 0;
      const chrome = laidOut ? Math.max(0, box.offsetHeight - el.offsetHeight) : 72 + pad;
      const natural = el.scrollHeight + chrome;
      setCanExpand(natural > min + 24);
      if (!userSetRef.current) {
        setMaxH(Math.round(Math.min(Math.max(natural, min), max)));
      } else {
        // A height the shopper dragged to still has to fit a viewport that has since shrunk.
        setMaxH((h) => (h != null && h > max ? Math.round(max) : h));
      }
    };
    measure();
    /* WHAT IS WATCHED IS WHAT GROWS: THE ROWS, NOT THE SCROLL BOX.
     *
     * Only the scroll box used to be observed. Once `maxH` is set, that box is held to the cap, so
     * whatever arrives AFTER the first measure — the "you may also like" row, fetched when the
     * drawer opens, and the photographs inside it — grows `scrollHeight` and not the box, and the
     * observer never fires. The first open after an add therefore stayed at the height of the
     * cart without its suggestions: short, scrolled. The second open found them already there and
     * opened right. Every child is observed, and children added later are picked up.
     *
     * And the viewport is re-read on `visualViewport` resize: an in-app browser changes it when its
     * toolbar moves, without always firing the window's `resize`. */
    const el = contentRef.current;
    let ro;
    let mo;
    if (typeof ResizeObserver !== 'undefined' && el) {
      ro = new ResizeObserver(() => measure());
      const observeRows = () => {
        ro.observe(el);
        Array.from(el.children).forEach((child) => ro.observe(child));
      };
      observeRows();
      if (typeof MutationObserver !== 'undefined') {
        mo = new MutationObserver(() => { observeRows(); measure(); });
        mo.observe(el, { childList: true });
      }
    }
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    window.addEventListener('resize', measure);
    if (vv && vv.addEventListener) vv.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      if (mo) mo.disconnect();
      window.removeEventListener('resize', measure);
      if (vv && vv.removeEventListener) vv.removeEventListener('resize', measure);
    };
  }, []);

  const onStart = useCallback((y) => {
    if (!enabled) return;
    startRef.current = { y, maxH: maxH != null ? maxH : px(MIN_VH) };
    setDragging(true);
  }, [enabled, maxH]);

  const onMove = useCallback((y) => {
    const s = startRef.current;
    if (s == null) return;
    const dy = s.y - y; // dragging up = positive
    const max = Math.min(px(MAX_VH), roomInOverlay(contentRef.current && contentRef.current.parentElement));
    const min = Math.min(px(MIN_VH), max);
    const next = s.maxH + dy;
    userSetRef.current = true;
    if (next >= max) { setMaxH(max); setTranslateY(0); return; }
    if (next <= min) { setMaxH(min); setTranslateY(min - next); return; } // past min → dismiss feel
    setMaxH(Math.round(next));
    setTranslateY(0);
  }, []);

  const onEnd = useCallback(() => {
    setDragging(false);
    const t = translateY;
    startRef.current = null;
    if (t > DISMISS_PX) {
      setTranslateY(visibleHeight());
      setTimeout(() => onDismiss && onDismiss(), 240);
      return;
    }
    setTranslateY(0);
  }, [translateY, onDismiss]);

  const bind = {
    onTouchStart: (e) => onStart(e.touches[0] ? e.touches[0].clientY : 0),
    onTouchMove: (e) => onMove(e.touches[0] ? e.touches[0].clientY : 0),
    onTouchEnd: onEnd,
    onPointerDown: (e) => { if (e.pointerType === 'mouse' && e.button === 0) onStart(e.clientY); },
    onPointerMove: (e) => { if (dragging && e.pointerType === 'mouse') onMove(e.clientY); },
    onPointerUp: (e) => { if (dragging && e.pointerType === 'mouse') onEnd(); },
  };

  const sheetStyle = {
    maxHeight: maxH != null ? `${maxH}px` : `${MAX_VH}vh`,
    transform: translateY ? `translateY(${translateY}px)` : undefined,
    transition: dragging
      ? 'none'
      : 'max-height 0.3s cubic-bezier(0.22, 1, 0.36, 1), transform 0.28s cubic-bezier(0.22, 1, 0.36, 1)',
  };

  // Back-compat: callers may still ask to snap; map to the cap.
  const setSnap = useCallback((s) => {
    userSetRef.current = true;
    const max = Math.min(px(MAX_VH), roomInOverlay(contentRef.current && contentRef.current.parentElement));
    setMaxH(s === 'expanded' ? max : Math.min(px(MIN_VH), max));
  }, []);

  return { contentRef, canExpand, bind, sheetStyle, setSnap, snap: 'free' };
}

export default useDraggableSheet;
